// public/js/http/method.js
// Einheitlicher Aufruf von Frappe-Methoden mit konsolidierten HTTP-Utils.

import { fetchJson, createIdempotencyKey } from "./utils.js";

const API_PREFIX = "/api/method/";
const IDEMPOTENCY_HEADER = "X-Idempotency-Key";
const READ_METHODS = new Set(["GET", "HEAD"]);

// Welle W-ERROR-TOAST-1 (2026-05-21):
// HTTP-Status-Codes der Backend-Wrapper-Schicht (json_err). Wenn payload.code
// fehlt fallen wir bei diesen Status-Werten auf einen kanonischen Code zurueck
// damit error.code IMMER gesetzt ist (Niko-CC Reason-Code-Routing).
const HTTP_STATUS_FALLBACK_CODES = Object.freeze({
  400: "bad_request",
  401: "unauthorized",
  403: "forbidden",
  404: "not_found",
  405: "method_not_allowed",
  408: "request_timeout",
  409: "conflict",
  410: "endpoint_removed",
  413: "payload_too_large",
  415: "unsupported_media_type",
  422: "validation_error",
  429: "rate_limited",
  500: "internal_error",
  502: "bad_gateway",
  503: "service_unavailable",
  504: "gateway_timeout",
});

// Welle W-ERROR-TOAST-1: Patterns die `err.message` als nicht-user-tauglich
// markieren (Roh-Transport-Fehler, technische JS-Errors). Wenn err.message
// matched, NICHT als Toast zeigen — Fallback verwenden.
const NON_USER_FACING_MESSAGE_PATTERNS = [
  /^HTTP \d/i,                       // Wrapper-Fallback "HTTP 500"
  /^TypeError:/i,                    // JS-Native
  /^Failed to fetch/i,               // Browser-Fetch-Native
  /^NetworkError/i,                  // Browser-Fetch-Native
  /^AbortError/i,                    // AbortController
  /^The operation was aborted/i,     // AbortController (Chrome)
  /^Load failed/i,                   // Safari-Fetch-Native
  /^[A-Z][a-zA-Z]+Error: /,          // Stack-Trace-Header
];

function toQuery(params = {}) {
  const query = new URLSearchParams(params).toString();
  return query ? `?${query}` : "";
}

function normalizeOptions(options = {}) {
  const opts = { ...options };
  const method = (opts.method || opts.type || "POST").toUpperCase();
  opts.method = method;
  opts.headers = { ...(opts.headers || {}) };
  return opts;
}

export async function callMethod(methodName, args = {}, options = {}) {
  if (typeof methodName !== "string" || !methodName.trim()) {
    throw new Error("callMethod requires a method name");
  }

  const opts = normalizeOptions(options);
  let url = `${API_PREFIX}${methodName}`;

  if (READ_METHODS.has(opts.method)) {
    url += toQuery(args || {});
  } else {
    opts.body = args ?? {};
    if (typeof createIdempotencyKey === "function" && !opts.headers[IDEMPOTENCY_HEADER]) {
      opts.headers[IDEMPOTENCY_HEADER] = createIdempotencyKey();
    }
  }

  const { res, payload } = await fetchJson(url, opts);

  if (!payload?.success) {
    const baseMessage = payload?.toast || `HTTP ${res?.status ?? "?"}`;
    const error = new Error(baseMessage);
    error.status = res?.status;
    error.payload = payload;
    error.method = methodName;
    // W-ERROR-TOAST-1: code IMMER setzen (auch wenn payload keinen Code hat),
    // damit Catch-Sites via err.code === 'feature_not_enabled' o.ae. routen
    // koennen ohne defensive payload-Existenz-Checks.
    error.code = payload?.code || HTTP_STATUS_FALLBACK_CODES[res?.status] || "request_failed";
    throw error;
  }

  return payload ?? {};
}

/**
 * Welle W-ERROR-TOAST-1 (2026-05-21) — SSOT-Helper.
 *
 * Extrahiert die kanonische User-Toast-Botschaft aus einem von callMethod()
 * geworfenen Error. Reihenfolge:
 *
 *   1. err.payload.toast        — Backend-json_err()-Envelope (kanonisch,
 *                                  DSGVO-clean, deutsch, sanitisiert)
 *   2. err.message              — nur wenn KEIN technischer Roh-Fehler
 *                                  (siehe NON_USER_FACING_MESSAGE_PATTERNS).
 *                                  Hilft beim `throw new Error(data.toast || ...)`-
 *                                  Pattern in Legacy-Code-Pfaden.
 *   3. fallback                 — Modulspezifischer deutscher Fallback-Text
 *                                  (z.B. "Archivieren fehlgeschlagen").
 *   4. "Aktion fehlgeschlagen"  — Generischer Last-Resort.
 *
 * Catch-Site-Pattern:
 *
 *   } catch (err) {
 *     toast.error(extractServerErrorToast(err, __('Foo fehlgeschlagen')));
 *   }
 *
 * NIEMALS direkt einen hardcoded Toast-String ohne Konsultation des
 * Server-Envelopes in einem catch-Block setzen — das verschluckt
 * feature_not_enabled / permission_denied / rate_limited / validation_error
 * Botschaften und zeigt dem User generisches "Netzwerkfehler" / "Fehler".
 *
 * @param {unknown} err       — geworfener Error (von callMethod oder fetchJson)
 * @param {string} [fallback] — Modulspezifischer User-Text fuer Genuine-Network-Fail
 * @returns {string}          — User-facing Toast-String (deutsch, kein Stack-Trace)
 */
export function extractServerErrorToast(err, fallback) {
  // 1) Backend-kanonisches Envelope (callMethod-Pfad aus http/method.js)
  const payloadToast = err && err.payload && err.payload.toast;
  if (typeof payloadToast === "string" && payloadToast.trim() !== "") {
    return payloadToast;
  }

  // 2) jqXHR-Pfad (frappe.call / $.ajax / window.citaFetchJson Rejection)
  //    Backend liefert HTTP 4xx + responseJSON. Cita-Middleware flattet
  //    {success,code,toast,data} entweder direkt oder als .message.{...}.
  if (err && err.responseJSON && typeof err.responseJSON === "object") {
    const rj = err.responseJSON;
    if (typeof rj.toast === "string" && rj.toast.trim() !== "") return rj.toast;
    if (rj.message && typeof rj.message === "object" && typeof rj.message.toast === "string" && rj.message.toast.trim() !== "") {
      return rj.message.toast;
    }
  }
  if (err && typeof err.responseText === "string" && err.responseText.trim() !== "") {
    try {
      const parsed = JSON.parse(err.responseText);
      if (parsed && typeof parsed === "object") {
        if (typeof parsed.toast === "string" && parsed.toast.trim() !== "") return parsed.toast;
        if (parsed.message && typeof parsed.message === "object" && typeof parsed.message.toast === "string" && parsed.message.toast.trim() !== "") {
          return parsed.message.toast;
        }
      }
    } catch (_) { /* not JSON, ignore */ }
  }

  // 3) err.message — aber nur wenn user-tauglich (keine HTTP-Status-Codes,
  //    keine Stack-Traces, keine native JS-Error-Header).
  const msg = err && err.message;
  if (typeof msg === "string" && msg.trim() !== "") {
    const isTechnical = NON_USER_FACING_MESSAGE_PATTERNS.some((re) => re.test(msg));
    if (!isTechnical) {
      return msg;
    }
  }

  // 4) Caller-spezifischer Fallback
  if (typeof fallback === "string" && fallback.trim() !== "") {
    return fallback;
  }

  // 5) Last-Resort — i18n-aware wenn frappe-translate verfuegbar ist.
  //    Frappe's window.__ ist eine no-op fuer noch nicht uebersetzte Strings,
  //    blockt aber Bundling-Pipelines die nach __()-Calls scannen mit, sodass
  //    der String im Pot-File landet (DE als Default, EN-Migration ready).
  const LAST_RESORT_DE = "Aktion fehlgeschlagen.";
  if (typeof window !== "undefined" && typeof window.__ === "function") {
    try {
      const translated = window.__(LAST_RESORT_DE);
      if (typeof translated === "string" && translated.trim() !== "") return translated;
    } catch (_) { /* Frappe-translate may throw on early boot — fall through */ }
  }
  return LAST_RESORT_DE;
}

export default { callMethod, extractServerErrorToast };
